import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PresignedUrl } from '@src/domain/pascos/value_objects/presigned_url';

/**
 * Hook: useGetPascoDownloadUrl
 * Fetches a presigned URL for downloading/viewing a PASCO file
 * 
 * @param pascoId - The unique identifier of the PASCO
 * @param options - React Query options
 * @returns Query result with presigned URL
 * 
 * @example
 * ```typescript
 * const { data: presignedUrl, isLoading } = useGetPascoDownloadUrl(pascoId, {
 *     enabled: !!pascoId,
 *     staleTime: 5 * 60 * 1000, // 5 minutes
 * });
 * 
 * if (presignedUrl) {
 *     window.open(presignedUrl.downloadUrl, '_blank');
 * }
 * ```
 */
export function useGetPascoDownloadUrl(
    pascoId: string | undefined,
    options?: Omit<
        UseQueryOptions<PresignedUrl, Error>,
        'queryKey' | 'queryFn'
    >
) {
    const pascoService = getPascoService();

    return useQuery({
        queryKey: ['pascos', pascoId, 'download-url'],
        queryFn: async () => {
            if (!pascoId) {
                throw new Error('Pasco ID is required');
            }
            return await pascoService.getDownloadUrl(pascoId);
        },
        enabled: !!pascoId && (options?.enabled ?? true),
        staleTime: 5 * 60 * 1000, // 5 minutes - URLs are valid for longer but we refresh to be safe
        gcTime: 10 * 60 * 1000, // 10 minutes - Cache for 10 minutes
        retry: 2,
        ...options,
    });
}
